import React from 'react';
import { Navigate } from '../../routes';
import { useAppStore } from '../../store';
import { UserRole } from '@repo/shared-types';
import { ROUTES } from '@repo/shared-types/routeRegistry';
import { OperatorOrgGate } from './OperatorOrgGate';
import { LoadingSpinner } from '../ui/LoadingSpinner';

type AuthGuardShellProps = {
  shell: true;
  children: React.ReactNode;
};

type AuthGuardRouteProps = {
  allowedRoles: UserRole[];
  children: React.ReactNode;
  blockSelfManagingOwner?: boolean;
};

export type AuthGuardProps = AuthGuardShellProps | AuthGuardRouteProps;

function isShellProps(props: AuthGuardProps): props is AuthGuardShellProps {
  return 'shell' in props && props.shell === true;
}

/** Route-level guard: Clerk + backend profile (or Vitest store-only persona). Shell variant skips RBAC and only enforces session + operator org (via `OperatorOrgGate`). */
export const AuthGuard: React.FC<AuthGuardProps> = (props) => {
  const isWebTest = import.meta.env.MODE === 'test';
  const { clerkSignedIn, userRole, isSelfManagingOwner, currentUserId, isAuthPending } = useAppStore();
  const hasBackendProfile = Boolean(currentUserId?.trim());
  const isAuthenticated = isWebTest ? hasBackendProfile : clerkSignedIn && hasBackendProfile;

  if (!isAuthenticated) {
    return <Navigate to={ROUTES.LOGIN} replace />;
  }

  if (isShellProps(props)) {
    return <OperatorOrgGate>{props.children}</OperatorOrgGate>;
  }

  // Avoid RBAC with stale default role (PM) while Clerk profile fetch is in flight.
  if (!isWebTest && isAuthPending) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const { allowedRoles, children, blockSelfManagingOwner } = props;
  if (!allowedRoles.includes(userRole)) {
    return <Navigate to="/app/unauthorized" replace />;
  }

  if (blockSelfManagingOwner && userRole === UserRole.PROPERTY_MANAGER && isSelfManagingOwner === true) {
    return <Navigate to="/app/unauthorized" replace />;
  }

  return <OperatorOrgGate>{children}</OperatorOrgGate>;
};
